import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { SUPPORTED_LOCALES, isSupportedLocale, localizePath, useLocale } from '@shared/i18n';

export type RouteLike = {
  path?: string;
  element?: ReactNode;
  children?: RouteLike[];
};

const SyncLocaleFromRoute = ({ children }: { children: ReactNode }) => {
  const params = useParams<{ locale?: string }>();
  const { locale, setLocale } = useLocale();
  const routeLocale = isSupportedLocale(params.locale) ? params.locale : null;

  useEffect(() => {
    if (routeLocale && routeLocale !== locale) {
      setLocale(routeLocale);
    }
  }, [locale, routeLocale, setLocale]);

  return <>{children}</>;
};

const wrapRouteElement = (element: ReactNode) => (
  <SyncLocaleFromRoute>{element}</SyncLocaleFromRoute>
);

export const localizeRoutes = <T extends RouteLike>(routes: T[]): T[] => {
  return routes.flatMap(route => {
    const localizedChildren = route.children ? localizeRoutes(route.children) : undefined;

    if (!route.path || route.path === '*') {
      return [
        {
          ...route,
          element: route.element ? wrapRouteElement(route.element) : route.element,
          children: localizedChildren,
        },
      ] as T[];
    }

    return SUPPORTED_LOCALES.map(locale => ({
      ...route,
      path: localizePath(route.path as string, locale),
      element: route.element ? wrapRouteElement(route.element) : route.element,
      children: localizedChildren,
    })) as T[];
  });
};
